import * as React from 'react';
import {Component} from 'react';
import classNames from 'classnames';
import {TransitionGroup, CSSTransition} from 'react-transition-group';
import {Icon, PriorityIcon} from '@priority-software/priority-style-react';
import {keyCodes} from '../../constants';
import {setIsNavigationEnabled} from '../../_shared/GWTBridge/invoke/genPage';
import {
    getTopDialogZindex,
    updateTopDialogZindex
} from '../../_shared/GWTBridge/invoke/priModalDlg';
import {isRTL} from '../../Localization';

interface SidePanelState {
    open: boolean;
}

interface AnimatedSidePanelProps {
    transitionName?: string;
    transitionEnterTimeout?: number;
    transitionLeaveTimeout?: number;
}

interface SidePanelProps extends AnimatedSidePanelProps {
    title: string;
    subTitle?: string;
    className?: string;
    onOpen(): boolean;
    onClose(closeCallback: () => void): boolean;
    content(props: InjectedSidePanelProps): JSX.Element;
}

export interface InjectedSidePanelProps {
    open: boolean;
    openPanel(): void;
    closePanel(): void;
    hidePanel(): void;
}

export class SidePanel extends Component<SidePanelProps, SidePanelState> {
    focusRef;

    constructor(props) {
        super(props);

        this.state = {
            open: false
        };

        this.open = this.open.bind(this);
        this.close = this.close.bind(this);
        this.hide = this.hide.bind(this);
        this.handleKeyDown = this.handleKeyDown.bind(this);
    }
    componentDidUpdate() {
        this.focus();
    }

    //close panel on clicking ESC button
    handleKeyDown(event) {
        if (this.state.open && event.keyCode === keyCodes.ESC) {
            event.stopPropagation();
            this.close();
        }
    }

    public close() {
        const {onClose} = this.props;
        if (onClose && onClose(this.hide) === false) {
            return;
        }
        //remove event listener for esc button
        document.removeEventListener('keydown', this.handleKeyDown, false);
        //disable navigation with keyboard
        setIsNavigationEnabled(true);
        this.hide();
        updateTopDialogZindex(false);
    }

    public hide() {
        this.setState({open: false});
    }

    public open() {
        const {onOpen} = this.props;
        if (onOpen && onOpen() === false) {
            return;
        }
        //event listener for esc button
        document.addEventListener('keydown', this.handleKeyDown, false);
        //enable navigation with keyboard
        setIsNavigationEnabled(false);
        updateTopDialogZindex(true);
        this.setState({open: true});
    }

    public focus() {
        this.focusRef && this.focusRef.focus();
    }

    render() {
        const {title, subTitle, className, content: Content} = this.props;
        const {
            transitionName = 'side-panel',
            transitionEnterTimeout = 500,
            transitionLeaveTimeout = 500
        } = this.props;
        const {open} = this.state;
        const panelClass = classNames('gr-panel', className);
        return (
            <TransitionGroup>
                {open && (
                    <CSSTransition
                        classNames={transitionName}
                        timeout={{enter: transitionEnterTimeout, exit: transitionLeaveTimeout}}
                    >
                        <div className="gr-blocker" style={{zIndex: getTopDialogZindex()}}>
                            <div className={panelClass}>
                                <div className="opacity-wrapper">
                                    <button
                                        className="button-wrapper"
                                        ref={i => (this.focusRef = i)}
                                    />
                                    <div className="header">
                                        <Icon
                                            icon={
                                                isRTL()
                                                    ? PriorityIcon.arrowRight
                                                    : PriorityIcon.arrowLeft
                                            }
                                            onClick={this.close}
                                            iconColor="primary-500"
                                            componentClasses="panel-button"
                                        />
                                        <div className="title-wrapper">
                                            {subTitle && (
                                                <div className="sub-title">{subTitle}</div>
                                            )}
                                            <div className="title">{title}</div>
                                        </div>
                                    </div>
                                    <Content
                                        open={open}
                                        openPanel={this.open}
                                        closePanel={this.close}
                                        hidePanel={this.hide}
                                        {...this.props}
                                    />
                                </div>
                            </div>
                        </div>
                    </CSSTransition>
                )}
            </TransitionGroup>
        );
    }
}

export default SidePanel;
